import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Edit2, Trash2, GripVertical, Save, 
  Loader2, ChevronDown, ChevronUp, FileText, HelpCircle
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Dropdown Select' },
  { value: 'radio', label: 'Radio Buttons' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'date', label: 'Date Picker' },
  { value: 'file', label: 'File Upload' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone Number' },
  { value: 'note', label: 'Notes / Instructions' },
];

const AdminProcessTemplateEdit = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Edit states
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [templateForm, setTemplateForm] = useState({ name: '', description: '', is_private: false });
  
  // Step states
  const [showStepModal, setShowStepModal] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [stepForm, setStepForm] = useState({ name: '', order_number: 1, is_required: true, allow_upload_documents: false, description: '' });
  
  // Question states
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [activeStepId, setActiveStepId] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    field_type: 'text',
    options: [],
    allow_multiple: false,
    display_order: 1,
    is_required: true,
    is_hidden: false,
    is_product_name: false
  });
  const [optionInput, setOptionInput] = useState('');
  
  // Expanded steps
  const [expandedSteps, setExpandedSteps] = useState({});

  const isMainAdmin = user?.role === 'admin' || user?.role === 'main_admin';

  useEffect(() => {
    fetchTemplate();
  }, [templateId]);

  const fetchTemplate = async () => {
    try {
      const res = await api.get(`/admin/process-templates/${templateId}`);
      setTemplate(res.data);
      setTemplateForm({
        name: res.data.name,
        description: res.data.description || '',
        is_private: res.data.is_private || false
      });
      // Expand first step by default
      if (res.data.steps?.length > 0) {
        setExpandedSteps({ [res.data.steps[0].id]: true });
      }
    } catch (error) {
      toast.error('Failed to load template');
      navigate('/admin/process-templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/process-templates/${templateId}`, templateForm);
      toast.success('Template updated');
      setEditingTemplate(false);
      fetchTemplate();
    } catch (error) {
      toast.error('Failed to update template');
    } finally {
      setSaving(false);
    }
  };

  // Step handlers
  const openAddStep = () => {
    const maxOrder = template?.steps?.length > 0 
      ? Math.max(...template.steps.map(s => s.order_number)) + 1 
      : 1;
    setStepForm({ name: '', order_number: maxOrder, is_required: true, allow_upload_documents: false, description: '' });
    setEditingStep(null);
    setShowStepModal(true);
  };

  const openEditStep = (step) => {
    setStepForm({
      name: step.name,
      order_number: step.order_number,
      is_required: step.is_required ?? true,
      allow_upload_documents: step.allow_upload_documents ?? false,
      description: step.description || ''
    });
    setEditingStep(step);
    setShowStepModal(true);
  };

  const handleSaveStep = async () => {
    if (!stepForm.name.trim()) {
      toast.error('Step name is required');
      return;
    }
    
    setSaving(true);
    try {
      if (editingStep) {
        await api.put(`/admin/process-templates/${templateId}/steps/${editingStep.id}`, stepForm);
        toast.success('Step updated');
      } else {
        await api.post(`/admin/process-templates/${templateId}/steps`, stepForm);
        toast.success('Step added');
      }
      setShowStepModal(false);
      fetchTemplate();
    } catch (error) {
      toast.error('Failed to save step');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStep = async (stepId) => {
    if (!window.confirm('Delete this step and all its questions?')) return;
    
    try {
      await api.delete(`/admin/process-templates/${templateId}/steps/${stepId}`);
      toast.success('Step deleted');
      fetchTemplate();
    } catch (error) {
      toast.error('Failed to delete step');
    }
  };

  // Question handlers
  const openAddQuestion = (stepId) => {
    const step = template?.steps?.find(s => s.id === stepId);
    const maxOrder = step?.questions?.length > 0
      ? Math.max(...step.questions.map(q => q.display_order)) + 1
      : 1;
    
    setQuestionForm({
      question_text: '',
      field_type: 'text',
      options: [],
      allow_multiple: false,
      display_order: maxOrder,
      is_required: true,
      is_hidden: false,
      is_product_name: false
    });
    setActiveStepId(stepId);
    setEditingQuestion(null);
    setShowQuestionModal(true);
  };

  const handleSaveQuestion = async () => {
    if (!questionForm.question_text.trim()) {
      toast.error('Question text is required');
      return;
    }
    
    if (['select', 'radio', 'checkbox'].includes(questionForm.field_type) && questionForm.options.length === 0) {
      toast.error('Please add at least one option');
      return;
    }
    
    setSaving(true);
    try {
      await api.post(`/admin/process-templates/${templateId}/steps/${activeStepId}/questions`, questionForm);
      toast.success('Question added');
      setShowQuestionModal(false);
      fetchTemplate();
    } catch (error) {
      toast.error('Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (stepId, questionId) => {
    if (!window.confirm('Delete this question?')) return;
    
    try {
      await api.delete(`/admin/process-templates/${templateId}/steps/${stepId}/questions/${questionId}`);
      toast.success('Question deleted');
      fetchTemplate();
    } catch (error) {
      toast.error('Failed to delete question');
    }
  };

  const addOption = () => {
    if (optionInput.trim()) {
      setQuestionForm({
        ...questionForm,
        options: [...questionForm.options, optionInput.trim()]
      });
      setOptionInput('');
    }
  };

  const removeOption = (index) => {
    setQuestionForm({
      ...questionForm,
      options: questionForm.options.filter((_, i) => i !== index)
    });
  };

  const toggleStepExpanded = (stepId) => {
    setExpandedSteps(prev => ({ ...prev, [stepId]: !prev[stepId] }));
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </Sidebar>
    );
  }

  if (!template) return null;

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/admin/process-templates')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          
          <div className="flex-1">
            {editingTemplate ? (
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                  className="text-2xl font-bold px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <button
                  onClick={handleSaveTemplate}
                  disabled={saving}
                  className="px-3 py-1 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setEditingTemplate(false)}
                  className="px-3 py-1 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-gray-900">{template.name}</h1>
                <span className="text-sm text-gray-500 px-2 py-0.5 bg-gray-100 rounded">v{template.version || 1}</span>
                {isMainAdmin && (
                  <button
                    onClick={() => setEditingTemplate(true)}
                    className="p-1.5 hover:bg-gray-100 rounded-lg"
                  >
                    <Edit2 className="w-4 h-4 text-gray-500" />
                  </button>
                )}
              </div>
            )}
            {template.description && !editingTemplate && (
              <p className="text-gray-500 mt-1">{template.description}</p>
            )}
          </div>
        </div>

        {/* Steps List */}
        <div className="space-y-4">
          {template.steps?.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="font-medium text-gray-700 mb-2">No Steps Yet</h3>
              <p className="text-gray-500 text-sm mb-4">Add steps to define your process template</p>
              <button
                onClick={openAddStep}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                <Plus className="w-4 h-4" />
                Add First Step
              </button>
            </div>
          ) : (
            <>
              {template.steps.map((step, idx) => (
                <div key={step.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                  {/* Step Header */}
                  <div 
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => toggleStepExpanded(step.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center text-orange-600 font-semibold text-sm">
                        {step.order_number}
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{step.name}</h3>
                        <span className="text-sm text-gray-500">{step.questions?.length || 0} questions</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      {isMainAdmin && (
                        <>
                          <button
                            onClick={(e) => { e.stopPropagation(); openEditStep(step); }}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                          >
                            <Edit2 className="w-4 h-4 text-gray-500" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteStep(step.id); }}
                            className="p-2 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </>
                      )}
                      {expandedSteps[step.id] ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                  </div>
                  
                  {/* Questions (Expanded) */}
                  {expandedSteps[step.id] && (
                    <div className="border-t border-gray-100 p-4 bg-gray-50/50">
                      {step.questions?.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">No questions in this step</p>
                      ) : (
                        <div className="space-y-2 mb-4">
                          {step.questions.map((q, qIdx) => (
                            <div 
                              key={q.id} 
                              className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-100"
                            >
                              <div className="flex items-center gap-3">
                                <span className="text-xs text-gray-400 w-6">{qIdx + 1}.</span>
                                <div>
                                  <p className="text-sm text-gray-800">{q.question_text}</p>
                                  <span className="text-xs text-gray-400">
                                    {FIELD_TYPES.find(f => f.value === q.field_type)?.label || q.field_type}
                                    {q.is_required && ' • Required'}
                                  </span>
                                </div>
                              </div>
                              {isMainAdmin && (
                                <button
                                  onClick={() => handleDeleteQuestion(step.id, q.id)}
                                  className="p-1.5 hover:bg-red-50 rounded"
                                >
                                  <Trash2 className="w-4 h-4 text-red-400" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {isMainAdmin && (
                        <button
                          onClick={() => openAddQuestion(step.id)}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-gray-500 hover:border-orange-300 hover:text-orange-600 transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Add Question
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
              
              {/* Add Step Button */}
              {isMainAdmin && (
                <button
                  onClick={openAddStep}
                  className="w-full flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed border-gray-200 rounded-2xl text-gray-500 hover:border-orange-300 hover:text-orange-600 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Add New Step
                </button>
              )}
            </>
          )}
        </div>

        {/* Step Modal */}
        {showStepModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl w-full max-w-md p-6">
              <h2 className="text-xl font-semibold mb-4">
                {editingStep ? 'Edit Step' : 'Add New Step'}
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Step Name</label>
                  <input
                    type="text"
                    value={stepForm.name}
                    onChange={(e) => setStepForm({...stepForm, name: e.target.value})}
                    placeholder="e.g., LOI / Soft Offer"
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Order Number</label>
                  <input
                    type="number"
                    value={stepForm.order_number}
                    onChange={(e) => setStepForm({...stepForm, order_number: parseInt(e.target.value) || 1})}
                    min={1}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description (Optional)</label>
                  <textarea
                    value={stepForm.description}
                    onChange={(e) => setStepForm({...stepForm, description: e.target.value})}
                    rows={2}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                  />
                </div>
                
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={stepForm.is_required}
                      onChange={(e) => setStepForm({...stepForm, is_required: e.target.checked})}
                      className="w-4 h-4 rounded text-orange-600"
                    />
                    <span className="text-sm">Required Step</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={stepForm.allow_upload_documents}
                      onChange={(e) => setStepForm({...stepForm, allow_upload_documents: e.target.checked})}
                      className="w-4 h-4 rounded text-orange-600"
                    />
                    <span className="text-sm">Allow Document Upload</span>
                  </label>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowStepModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveStep}
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : (editingStep ? 'Update Step' : 'Add Step')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Question Modal */}
        {showQuestionModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl w-full max-w-lg p-6 my-8">
              <h2 className="text-xl font-semibold mb-4">Add Question</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Question Text</label>
                  <textarea
                    value={questionForm.question_text}
                    onChange={(e) => setQuestionForm({...questionForm, question_text: e.target.value})}
                    placeholder="Enter your question..."
                    rows={2}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                    <select
                      value={questionForm.field_type}
                      onChange={(e) => {
                        const newType = e.target.value;
                        const updates = { field_type: newType };
                        if (newType === 'note') updates.is_required = false;
                        setQuestionForm({...questionForm, ...updates});
                      }}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      {FIELD_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                    <input
                      type="number"
                      value={questionForm.display_order}
                      onChange={(e) => setQuestionForm({...questionForm, display_order: parseInt(e.target.value) || 1})}
                      min={1}
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>
                
                {/* Note type hint */}
                {questionForm.field_type === 'note' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <p className="text-sm text-blue-700">
                      <strong>Notes / Instructions:</strong> This will display as formatted text to users.
                      Users cannot input anything — it's for displaying information, instructions, or descriptions between questions. HTML is supported.
                    </p>
                  </div>
                )}

                {/* Options for select/radio/checkbox */}
                {['select', 'radio', 'checkbox'].includes(questionForm.field_type) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Options</label>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        value={optionInput}
                        onChange={(e) => setOptionInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
                        placeholder="Add an option..."
                        className="flex-1 px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      <button
                        type="button"
                        onClick={addOption}
                        className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
                      >
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {questionForm.options.map((opt, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-3 py-1 bg-orange-50 text-orange-700 rounded-full text-sm">
                          {opt}
                          <button onClick={() => removeOption(i)} className="hover:text-red-600">&times;</button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={questionForm.is_required}
                      onChange={(e) => setQuestionForm({...questionForm, is_required: e.target.checked})}
                      className="w-4 h-4 rounded text-orange-600"
                    />
                    <span className="text-sm">Required</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={questionForm.is_hidden}
                      onChange={(e) => setQuestionForm({...questionForm, is_hidden: e.target.checked})}
                      className="w-4 h-4 rounded text-orange-600"
                    />
                    <span className="text-sm">Hidden</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={questionForm.is_product_name}
                      onChange={(e) => setQuestionForm({...questionForm, is_product_name: e.target.checked})}
                      className="w-4 h-4 rounded text-orange-600"
                    />
                    <span className="text-sm">Is Product Name</span>
                  </label>
                  
                  {['select', 'checkbox'].includes(questionForm.field_type) && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={questionForm.allow_multiple}
                        onChange={(e) => setQuestionForm({...questionForm, allow_multiple: e.target.checked})}
                        className="w-4 h-4 rounded text-orange-600"
                      />
                      <span className="text-sm">Allow Multiple</span>
                    </label>
                  )}
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowQuestionModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveQuestion}
                  disabled={saving}
                  className="flex-1 px-4 py-2.5 bg-orange-600 text-white rounded-xl hover:bg-orange-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Add Question'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
};

export default AdminProcessTemplateEdit;
